import { useEffect, useMemo, useState } from "react";
import Filters from "../../common/Filters/Filters";
import SearchBoxWithCustomQuery from "../../common/SearchBoxWithCustomQuery";
import Table from "../../common/Table/Table";
import {
  formatDateDDMMYYYY,
  formatDateWithSuffix,
} from "../../helpers/functions/dateFormatter";
import { exportToExcel } from "../../helpers/functions/exportToExcel";
import { excelDataFormatter } from "../../helpers/functions/formatters";
import { getDataFromDataKey } from "../../helpers/functions/getDataFromKey";
import ButtonTabBar from "../../ui/ButtonTabBar";
import AddEditLeadDetails from "./AddEditLeadDetails";
import { LEAD_EXCEL_HEADERS } from "./constants/LeadExcelHeaders";
import useLeadManagement from "./hooks/useLeadManagement";
import ViewRemarksModal from "./ViewRemarksModal";

function LeadManagement() {
  const {
    leadList,
    loading,
    handleChange,
    setRefetch,
    handleSubmit,
    setLeadType,
    setModalData,
    setIsEdit,
    setShowModal,
    showModal,
    modalData,
    initialValue,
    isEdit,
    setFilteredData,
    statesList,
    showRemarksModal,
    setShowRemarksModal,
    remarksList,
    loadingRemarks,
    handleRemarks,
    submitLoading,
    filteredData,
    leadType,
    tabsCount,
    leadCounts,
    handleCloseLead,
    leadSourceOptions,
    handleLeadSourceChange,
    leadSource,
    handleSourceFilter,
    handleEditLead,
    existingLead,
    lookupLoading,
    claiming,
    handleTakeOwnership,
    revealedPhone,
    revealingPhoneLeadId,
    handleRevealPhone,
  } = useLeadManagement();

  const [followupBucket, setFollowupBucket] = useState("overdue");
  const [ownerFilter, setOwnerFilter] = useState("All");

  const FOLLOWUP_SUBTABS = [
    { id: "overdue", name: "Overdue", badge: "danger" },
    { id: "today", name: "Today", badge: "warning" },
    { id: "upcoming", name: "Upcoming", badge: "primary" },
    { id: "unscheduled", name: "Needs scheduling", badge: "secondary" },
  ];

  // Owner options (latest actioner, fallback creator) built from the loaded tab.
  const ownerOptions = useMemo(() => {
    const m = new Map();
    (leadList || []).forEach((row) => {
      const key = row?.owner_id ?? "unassigned";
      const entry = m.get(key) || {
        id: row?.owner_id ?? "unassigned",
        name: row?.owner || "Unassigned",
        count: 0,
      };
      entry.count += 1;
      m.set(key, entry);
    });
    return [...m.values()].sort((a, b) => b.count - a.count);
  }, [leadList]);

  // Reset the owner filter when switching tabs (owners differ per queue).
  useEffect(() => {
    setOwnerFilter("All");
  }, [leadType]);

  // On the Follow-ups tab, show only the rows for the selected sub-bucket; then
  // apply the owner filter on top.
  const displayedData = (
    leadType === "followups"
      ? (filteredData || []).filter((row) => row?.bucket === followupBucket)
      : filteredData || []
  ).filter(
    (row) =>
      ownerFilter === "All" ||
      String(row?.owner_id ?? "unassigned") === String(ownerFilter),
  );

  const notFoundTextByLeadType = {
    new: "No new leads",
    followups: "No follow up leads",
    closed: "No closed leads",
  };

  const tabsList = useMemo(
    () => [
      {
        id: "new",
        name: "New",
        countBadgeType: "primary",
      },
      {
        id: "followups",
        name: "Follow Ups",
        countBadgeType: "danger",
      },
      {
        id: "closed",
        name: "Closed",
        countBadgeType: "success",
      },
    ],
    [],
  );

  const filtersList = useMemo(
    () => [
      { key: "inquiry.state", name: "State", id: "state" },
      { key: "inquiry.city", name: "City", id: "city" },
      { key: "plan_interest", name: "Plan", id: "plan_interest" },
      { key: "inquiry.inquiry_type", name: "Source", id: "lead_source" },
      {
        key: "segment",
        name: "Segment",
        id: "segment",
        valueType: "array",
        filterValues: ["2W", "3W", "Car", "CV", "Tractor"],
      },
    ],
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: "#",
        data_key: "index",
        render: (value, index) => {
          return <span className=" text-muted">{index + 1}</span>;
        },
      },
      {
        title: "Name",
        data_key: "inquiry.name",
        render: (value) => {
          const phone = getDataFromDataKey(value, "inquiry.phone");
          const isMasked = /\D/.test(phone || "");
          const isRevealedHere = revealedPhone?.leadId === value.lead_id;
          const isRevealing = revealingPhoneLeadId === value.lead_id;
          const displayPhone =
            isMasked && isRevealedHere ? revealedPhone.phone : phone;
          const showAsLink = !isMasked || isRevealedHere;
          return (
            <>
              <span className=" fw-semibold text-start">
                {getDataFromDataKey(value, "inquiry.name")}
              </span>
              <br />
              {showAsLink ? (
                <a
                  href={`tel:${displayPhone}`}
                  className=" text-capitalize text-nowrap text-muted text-decoration-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i class="bi bi-telephone-fill"></i> {displayPhone}
                </a>
              ) : (
                <span
                  className=" text-capitalize text-nowrap text-muted cursor-pointer"
                  onClick={() => handleRevealPhone(value.lead_id)}
                  title="Click to reveal the full number"
                >
                  <i class="bi bi-telephone-fill"></i> {phone}{" "}
                  {isRevealing ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    <i className="bi bi-eye-fill text-primary"></i>
                  )}
                </span>
              )}
            </>
          );
        },
      },

      {
        title: "State",
        data_key: "inquiry.state",
        render: (value) => {
          return (
            <span className=" text-capitalize text-nowrap">
              <i class="bi bi-geo-alt-fill"></i>{" "}
              {getDataFromDataKey(value, "inquiry.state")}
            </span>
          );
        },
      },

      // {
      //   title: "Added On",
      //   data_key: "inquiry.added_on",
      //   render: (value) => {
      //     return (
      //       <span>
      //         {formatDateWithSuffix(new Date(value?.inquiry?.added_on))}
      //       </span>
      //     );
      //   },
      // },
      {
        title: "Segment",
        data_key: "segment",
        render: (value) => {
          return (
            <span className=" text-capitalize">
              {value?.segment ? value?.segment.join(", ") : value?.segment}
            </span>
          );
        },
      },
      {
        title: "Source / Interest",
        data_key: "plan_interest",
        render: (value) => {
          return (
            <div className=" d-flex flex-column align-items-start gap-1 flex-wrap">
              <span
                className={`badge fw-semibold bg-primary-subtle text-primary`}
              >
                {value?.plan_interest}
              </span>
              {value?.ta_interest && (
                <span className=" badge fw-semibold bg-success-subtle text-success ">
                  Interested In TA
                </span>
              )}
              <span className=" badge fw-semibold bg-danger-subtle text-danger ">
                {value?.inquiry?.inquiry_type}
              </span>
            </div>
          );
        },
      },
      {
        title: "Follow Up Date",
        data_key: "follow_up_date",
        render: (value) => {
          if (!value?.follow_up_date) {
            return (
              <span className=" text-muted text-capitalize">
                No follow up date
              </span>
            );
          }
          const followUpDate = formatDateDDMMYYYY(
            new Date(value?.follow_up_date),
          );
          const today = formatDateDDMMYYYY(new Date());
          return (
            <span className=" text-nowrap">
              {followUpDate === today ? (
                <span className=" fw-bold text-danger bg-danger-subtle badge text-nowrap">
                  {" "}
                  Today
                </span>
              ) : (
                followUpDate
              )}{" "}
            </span>
          );
        },
      },
      {
        title: "Comments",
        data_key: "remark_count",
        sortable: true,
        render: (value) => {
          if (value?.remark_count === 0) {
            return (
              <span className=" text-muted text-danger">
                <i className="bi bi-chat-dots-fill"></i> No comments yet.
              </span>
            );
          }

          return (
            <div className=" ">
              <div className=" d-flex align-items-center  text-sm">
                <span>{value?.last_remark_by}</span>

                {value?.last_remark_date ? (
                  <>
                    <span>
                      {" "}
                      <i className=" bi bi-dot"></i>{" "}
                    </span>
                    <span className=" text-nowrap">
                      {" "}
                      <i className=" bi bi-calendar"></i>{" "}
                      {formatDateDDMMYYYY(new Date(value?.last_remark_date))}
                    </span>
                  </>
                ) : (
                  ""
                )}

                {value?.remark_count > 0 && (
                  <>
                    <span>
                      {" "}
                      <i className=" bi bi-dot text-sm"></i>{" "}
                    </span>
                    <span
                      className=" text-nowrap bg-primary-subtle text-primary rounded-1 p-1 cursor-pointer"
                      onClick={() => {
                        setModalData({
                          name: value?.inquiry?.name,
                          phone: value?.inquiry?.phone,
                        });
                        handleRemarks(value?.lead_id);
                      }}
                    >
                      <i
                        class="bi bi-chat-dots-fill"
                        style={{ maxHeight: "12px" }}
                      ></i>{" "}
                      <span className=" text-sm">{value?.remark_count}</span>
                    </span>
                  </>
                )}
              </div>
              <div className="">
                <span className=" text-sm">{value?.last_remark}</span>
              </div>
            </div>
          );
        },
      },

      leadType !== "closed" && {
        title: "Action",
        data_key: "inquiry.added_on",
        sortable: true,
        // style: { zIndex: 9999 },
        render: (value) => {
          return (
            <>
              <div class="dropdown mb-1">
                <span
                  class=" bg-success-subtle text-success fw-semibold badge text-nowrap cursor-pointer text-sm"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Action
                  <i className=" bi bi-caret-down-fill"></i>
                </span>
                <ul class="dropdown-menu p-1 border shadow-sm">
                  <li
                    className=" text-capitalize text-md cursor-pointer"
                    onClick={() => {
                      handleEditLead(value);
                    }}
                  >
                    <span className="dropdown-item text-nowrap text-sm">
                      <i className=" bi bi-pencil-square text-secondary cursor-pointer"></i>{" "}
                      Edit
                    </span>
                  </li>
                  <li
                    className=" text-capitalize text-md cursor-pointer"
                    onClick={() => {
                      handleCloseLead(value?.lead_id);
                    }}
                  >
                    <span className="dropdown-item text-nowrap text-sm">
                      <i className="  bi bi-check-circle-fill text-success cursor-pointer"></i>{" "}
                      Close Lead
                    </span>
                  </li>
                </ul>
              </div>
              <span className=" mt-1 text-muted" style={{ fontSize: "0.7rem" }}>
                Added On{" "}
                {formatDateWithSuffix(new Date(value?.inquiry?.added_on))}
              </span>
            </>
          );
        },
      },
    ],
    [
      leadType,
      handleEditLead,
      handleRemarks,
      setModalData,
      handleCloseLead,
      revealedPhone,
      revealingPhoneLeadId,
      handleRevealPhone,
    ],
  );

  return (
    <>
      {showModal && (
        <AddEditLeadDetails
          show={showModal}
          onHide={() => setShowModal(false)}
          modalData={modalData}
          setModalData={setModalData}
          handleChange={handleChange}
          isEdit={isEdit}
          handleSubmit={handleSubmit}
          statesList={statesList}
          loading={submitLoading}
          existingLead={existingLead}
          lookupLoading={lookupLoading}
          claiming={claiming}
          handleTakeOwnership={handleTakeOwnership}
        />
      )}
      {showRemarksModal && (
        <ViewRemarksModal
          show={showRemarksModal}
          onHide={() => setShowRemarksModal(false)}
          user={modalData}
          data={remarksList}
          loading={loadingRemarks}
        />
      )}

      <div
        className=""
        style={{
          minHeight: "400px",
          overflowY: "auto",
          fontFamily: "Plus Jakarta Sans",
        }}
      >
        <ButtonTabBar
          tabsList={tabsList}
          tabsCount={tabsCount}
          tabIdKey={"leadsListTabId"}
          onClick={(tabId) => {
            setLeadType(tabId);
            setRefetch(true);
            if (tabId === "followups") setFollowupBucket("overdue");
          }}
        />

        {leadType === "followups" && (
          <div className="d-flex gap-2 flex-wrap mt-3">
            {FOLLOWUP_SUBTABS.map((st) => {
              const active = followupBucket === st.id;
              return (
                <button
                  key={st.id}
                  type="button"
                  className={`btn btn-sm d-flex align-items-center gap-2 ${
                    active ? `btn-${st.badge}` : `btn-outline-${st.badge}`
                  }`}
                  onClick={() => setFollowupBucket(st.id)}
                >
                  {st.name}
                  <span
                    className={`badge ${
                      active
                        ? "bg-light text-dark"
                        : `bg-${st.badge}-subtle text-${st.badge}`
                    }`}
                  >
                    {leadCounts?.[st.id] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div
          // style={{ background: "rgb(230, 249, 255)" }}
          className=" mt-3 py-2 px-2"
        >
          <div className=" d-flex justify-content-lg-between flex-lg-row align-items-lg-center flex-column flex-wrap mt-2 gap-2 mb-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div class="dropdown">
                <span
                  class=" bg-dark text-light fw-semibold badge text-nowrap cursor-pointer text-md"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {leadSource?.option}{" "}
                  {leadSource?.count ? `(${leadSource?.count})` : ""}{" "}
                  <i className=" bi bi-caret-down-fill"></i>
                </span>
                <ul class="dropdown-menu p-0 border shadow-sm">
                  {Object.entries(leadSourceOptions)?.map(([option, count]) => (
                    <li
                      key={option}
                      className=" text-capitalize text-md cursor-pointer"
                      onClick={() => handleLeadSourceChange({ option, count })}
                    >
                      <span className="dropdown-item d-flex justify-content-between align-items-center gap-2">
                        <span>{option}</span>
                        <span className="badge fw-bold bg-primary-subtle text-primary ">
                          {count}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Filter the list by owner (latest actioner, matching the dashboard). */}
              <select
                className="form-select form-select-sm w-auto text-md"
                aria-label="Filter by owner"
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                style={{ maxWidth: 220 }}
              >
                <option value="All">All owners</option>
                {ownerOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.count})
                  </option>
                ))}
              </select>
            </div>
            <div className=" d-flex justify-content-start justify-content-lg-end align-items-center gap-2 flex-wrap">
              <SearchBoxWithCustomQuery
                allData={leadList}
                return_array={true}
                setFilteredData={setFilteredData}
                keysToSearchOn={[
                  "inquiry.name",
                  "inquiry.phone",
                  "inquiry.state",
                ]}
                useSearchBoxButton={true}
              />

              <Filters
                data={leadList}
                setData={setFilteredData}
                filteredData={filteredData}
                setFilteredData={setFilteredData}
                filtersList={filtersList}
                leadSource={leadSource}
                handleSourceChange={handleSourceFilter}
              />

              {displayedData && displayedData.length > 0 && (
                <button
                  className=" btn bg-danger-subtle text-danger rounded-circle"
                  onClick={() => {
                    const formattedData = excelDataFormatter(
                      LEAD_EXCEL_HEADERS,
                      displayedData,
                    );
                    exportToExcel(formattedData, `${leadType} Leads`);
                  }}
                  title="Download Excel"
                >
                  <i className="bi bi-download"></i>
                </button>
              )}

              <button
                className=" btn bg-danger-subtle text-danger rounded-circle"
                onClick={() => {
                  setRefetch(true);
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      class="spinner-border spinner-border-sm "
                      aria-hidden="true"
                    ></span>
                    <span class="visually-hidden" role="status">
                      Loading...
                    </span>
                  </>
                ) : (
                  <i class="bi bi-arrow-clockwise"></i>
                )}
              </button>
              <button
                className=" btn btn-primary rounded-circle"
                onClick={() => {
                  setShowModal(true);
                  setModalData(initialValue);
                  setIsEdit(false);
                }}
              >
                <i className="bi bi-plus"></i>
              </button>
            </div>
          </div>

          <Table
            columns={columns.filter((col) => col)}
            data={displayedData}
            th_text_position="start"
            td_text_position="start"
            maxTableHeight="80vh"
            enablePagination={true}
            defaultRowsPerPage={25}
            rowsPerPageOptions={[25, 50, 100, 200]}
            loading={loading}
            loading_rows={5}
            loading_cols={columns.length}
            no_data_msg={notFoundTextByLeadType[leadType] || "No leads found"}
          />
        </div>
      </div>
    </>
  );
}

export default LeadManagement;
